import { CHANNEL_ORDER, CHANNELS } from "../../../shared/goals/channels";
import type { GoalConfig, Pin } from "../../../shared/goals/config";
import { daysLeft, fmt, longDay, parseDay, usd, usd0 } from "./text";

/**
 * Edit goal on a running goal ends on its changes: what changed against the version in force, what would
 * stop it being applied, and what happens to what is running (the prototype's edChanges, edProblems and
 * edEffects, written from the goal's config instead of the prototype's sample state).
 */

export interface DiffLine {
  kind: "add" | "rm" | "mod";
  text: string;
}

export interface Change {
  k: "goal" | "by" | "text" | "budget" | "max" | "chOn" | "chOff" | "mkAdd" | "mkRm" | "card" | "pb" | "pbSwap" | "skill" | "skEd";
  lines: DiffLine[];
  channel?: string;
  market?: string;
}

const city = (m: string) => m.split(",")[0].trim();
const byText = (d: string) => longDay(parseDay(d));
const same = (a: unknown, b: unknown) => JSON.stringify(a ?? null) === JSON.stringify(b ?? null);
const playbook = (c: GoalConfig) => c.library.find((p) => p.kind === "playbook") ?? null;
const skills = (c: GoalConfig) => new Map(c.library.filter((p) => p.kind === "skill").map((p) => [p.name, p] as const));

export function changes(base: GoalConfig, next: GoalConfig): Change[] {
  const out: Change[] = [];
  const add = (k: Change["k"], lines: DiffLine[], extra: Partial<Change> = {}) => out.push({ k, lines, ...extra });
  if (base.result.target !== next.result.target)
    add("goal", [
      { kind: "rm", text: `Goal: ${fmt(base.result.target)} ${base.result.label}` },
      { kind: "add", text: `Goal: ${fmt(next.result.target)} ${next.result.label}` },
    ]);
  if (base.result.by !== next.result.by)
    add("by", [
      { kind: "rm", text: `Deadline: ${byText(base.result.by)}` },
      { kind: "add", text: `Deadline: ${byText(next.result.by)}` },
    ]);
  if (base.result.target === next.result.target && base.result.by === next.result.by && base.sentence.trim() !== next.sentence.trim())
    add("text", [{ kind: "mod", text: `Goal reworded: ${next.sentence.trim()}` }]);
  if (base.money.budgetCents !== next.money.budgetCents)
    add("budget", [
      { kind: "rm", text: `Total budget: ${usd(base.money.budgetCents)}` },
      { kind: "add", text: `Total budget: ${usd(next.money.budgetCents)}` },
    ]);
  if (base.money.perResultMaxCents !== next.money.perResultMaxCents)
    add("max", [
      { kind: "rm", text: `Most per result: ${usd(base.money.perResultMaxCents)}` },
      { kind: "add", text: `Most per result: ${usd(next.money.perResultMaxCents)}` },
    ]);
  for (const k of CHANNEL_ORDER) {
    const was = !!base.channels[k]?.on;
    const now = !!next.channels[k]?.on;
    if (was !== now) add(now ? "chOn" : "chOff", [{ kind: now ? "add" : "rm", text: `Channel: ${CHANNELS[k].label}` }], { channel: k });
  }
  const bm = base.channels.email?.markets ?? [];
  const nm = next.channels.email?.on ? (next.channels.email?.markets ?? []) : bm;
  for (const m of nm.filter((x) => !bm.includes(x))) add("mkAdd", [{ kind: "add", text: `Cold email market: ${m}` }], { market: m });
  for (const m of bm.filter((x) => !nm.includes(x))) add("mkRm", [{ kind: "rm", text: `Cold email market: ${m}` }], { market: m });
  for (const k of CHANNEL_ORDER) {
    if (!next.channels[k]?.on) continue;
    if (!same(base.channels[k]?.card, next.channels[k]?.card) || !same(base.channels[k]?.rules, next.channels[k]?.rules))
      add("card", [{ kind: "mod", text: `${CHANNELS[k].label} card: your edits` }], { channel: k });
  }
  const bp = playbook(base);
  const np = playbook(next);
  if (bp?.itemId !== np?.itemId) add("pbSwap", [{ kind: "mod", text: `Playbook: ${np?.name ?? "none"} v${np?.version ?? ""}`.trim() }]);
  else if (!same(bp?.edits, np?.edits)) add("pb", [{ kind: "mod", text: "Playbook: your edits for this goal" }]);
  const bs = skills(base);
  const ns = skills(next);
  for (const [name, p] of ns) {
    const b: Pin | undefined = bs.get(name);
    if (b && b.version !== p.version) add("skill", [{ kind: "mod", text: `${name}: v${b.version} to v${p.version}` }]);
  }
  if ([...ns].some(([name, p]) => !same(bs.get(name)?.edits, p.edits))) add("skEd", [{ kind: "mod", text: "Skills: your edits for this goal" }]);
  return out;
}

/** What has to be fixed before the changes can apply. */
export function problems(next: GoalConfig, spentCents: number, now = new Date()): string[] {
  const out: string[] = [];
  if (!(next.result.target > 0)) out.push("The goal needs a number, like 60.");
  if (!daysLeft(next.result.by, now)) out.push("The date has to be after today.");
  if (next.money.budgetCents < spentCents) out.push(`The budget has to be at least the ${usd(spentCents)} already spent.`);
  if (!(next.money.perResultMaxCents > 0)) out.push("Add the most per result, above $0.");
  if (!CHANNEL_ORDER.some((k) => next.channels[k]?.on)) out.push("Keep at least one channel.");
  return out;
}

export interface Running {
  /** Results so far and the pace lately, from the goal's results. */
  got: number;
  weekly: number | null;
  spentCents: number;
  paused: boolean;
}

/** What happens to what is running. Each line says only what Flashadz does; HTML-free, with **bold**. */
export function effects(base: GoalConfig, next: GoalConfig, ch: Change[], r: Running, now = new Date()): string[] {
  const out: string[] = [];
  const has = (k: Change["k"]) => ch.some((c) => c.k === k);
  if (has("goal") || has("by")) {
    const left = daysLeft(next.result.by, now);
    if (left > 0) {
      const wk = Math.max(0, (next.result.target - r.got) / (left / 7));
      const fine = r.weekly !== null && r.weekly >= wk;
      out.push(
        `The pace becomes about **${wk.toFixed(1)} a week** to reach ${fmt(next.result.target)} by ${byText(next.result.by)}.${
          r.weekly === null ? "" : ` You're at ${fmt(r.weekly)} a week, ${fine ? "so nothing live has to change." : "so expect the next review to suggest more money or new ideas, for your OK."}`
        }`,
      );
    }
  }
  if (has("budget")) {
    const left = next.money.budgetCents - r.spentCents;
    const days = daysLeft(next.result.by, now);
    const step = next.rules.stepUpPct;
    out.push(
      next.money.budgetCents > base.money.budgetCents
        ? `${usd0(left)} is left. The daily budget can rise ${next.money.dailyMaxCents ? `toward your ${usd(next.money.dailyMaxCents)} a day, ` : ""}about ${step}% at a time, so Meta keeps its learning.`
        : `${usd0(left)} is left${days ? `, which lasts to ${byText(next.result.by)} at about ${usd(Math.floor(left / days / 100) * 100)} a day` : ""}. If the ads would spend faster, the next morning review lowers the daily budget.`,
    );
  }
  if (has("max")) out.push(`From the next review, ads are judged against **${usd(next.money.perResultMaxCents)} a result**. Your stop rule stays as it is.`);
  for (const c of ch.filter((x) => x.k === "chOff"))
    out.push(
      c.channel === "meta"
        ? "The ads turn off within a minute. Nothing is deleted, so they keep their learning if you turn Meta back on."
        : c.channel === "email"
          ? "Cold email pauses within a minute: no new emails go out."
          : `${CHANNELS[c.channel as keyof typeof CHANNELS]?.label ?? c.channel} stops.`,
    );
  for (const c of ch.filter((x) => x.k === "chOn"))
    out.push(`${CHANNELS[c.channel as keyof typeof CHANNELS]?.label ?? c.channel} needs its own setup first: connect its account and approve what it sends.`);
  for (const c of ch.filter((x) => x.k === "mkAdd")) out.push(`${city(c.market ?? "")} joins this goal's cold email. Its first emails wait for your OK.`);
  for (const c of ch.filter((x) => x.k === "mkRm")) out.push(`No new emails go to ${city(c.market ?? "")}.`);
  if (ch.some((c) => ["card", "pb", "pbSwap", "skill", "skEd", "text"].includes(c.k)))
    out.push("Your edits to the goal, the channel cards, the playbook or the skills apply from the next review. Running ads keep running until a test calls them.");
  if (next.channels.meta?.on && !ch.some((c) => c.k === "chOff" && c.channel === "meta")) out.push("The ads keep their learning: same campaign, same ad set. Nothing is rebuilt.");
  out.push(r.paused ? "Everything stays paused until you resume it." : "Nothing new spends or sends until you approve it.");
  return out;
}

/** The version's one line, which the chat shows as "You changed the goal (version N): ...". */
export function summary(ch: Change[]): string {
  const s = `${ch.map((c) => c.lines[c.lines.length - 1].text).join("; ")}.`;
  return s.length > 300 ? `${s.slice(0, 297).trimEnd()}...` : s;
}
